import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import type { AssistantPageContext } from '@app-types/assistant.ts';

export const GENERIC_ASSISTANT_CONTEXT: AssistantPageContext = {
  area: 'generic',
  module: 'assistant',
  screen: 'generic',
  mode: 'safe',
};

export const inferAssistantPageContextFromPath = (pathname: string): AssistantPageContext => {
  const normalizedPath = pathname.replace(/\/+/g, '/').replace(/\/$/, '') || '/';

  if (normalizedPath === '/eshop/catalogue' || normalizedPath.startsWith('/eshop/category')) {
    return {
      area: 'eshop',
      module: 'catalogue',
      screen: 'public_catalogue',
      mode: 'browse',
      url: normalizedPath,
    };
  }

  if (normalizedPath === '/eshop/cart') {
    return {
      area: 'eshop',
      module: 'cart',
      screen: 'cart',
      mode: 'review',
      url: normalizedPath,
    };
  }

  if (normalizedPath === '/eshop/checkout') {
    return {
      area: 'eshop',
      module: 'checkout',
      screen: 'checkout',
      mode: 'checkout',
      url: normalizedPath,
    };
  }

  if (normalizedPath === '/dashboard/settings' || normalizedPath === '/erp/configuration/parameters') {
    return {
      area: 'dashboard',
      module: 'settings',
      screen: 'site_settings',
      mode: 'staff_view',
      url: normalizedPath,
    };
  }

  if (
    normalizedPath === '/eshop/dashboard/items/overview' ||
    normalizedPath.startsWith('/eshop/dashboard/items') ||
    normalizedPath === '/erp/services/catalogue' ||
    normalizedPath.startsWith('/erp/services/catalogue')
  ) {
    return {
      area: 'dashboard',
      module: 'catalog',
      screen: 'catalog_items',
      mode: 'staff_view',
      url: normalizedPath,
    };
  }

  return {
    ...GENERIC_ASSISTANT_CONTEXT,
    url: normalizedPath,
  };
};

export interface AssistantPageContextValue {
  declaredContext: AssistantPageContext | null;
  effectiveContext: AssistantPageContext;
  registerPageContext: (context: AssistantPageContext) => () => void;
}

const AssistantPageContextState = createContext<AssistantPageContextValue>({
  declaredContext: null,
  effectiveContext: GENERIC_ASSISTANT_CONTEXT,
  registerPageContext: () => () => undefined,
});

export const AssistantPageContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const [declaredContext, setDeclaredContext] = useState<AssistantPageContext | null>(null);

  const inferredContext = useMemo(
    () => inferAssistantPageContextFromPath(location.pathname),
    [location.pathname],
  );

  const registerPageContext = useCallback((context: AssistantPageContext) => {
    const normalizedContext = {
      ...context,
      url: context.url ?? location.pathname,
    };
    setDeclaredContext(normalizedContext);
    return () => {
      setDeclaredContext((current) => (current === normalizedContext ? null : current));
    };
  }, [location.pathname]);

  const effectiveContext = declaredContext ?? inferredContext;

  const value = useMemo<AssistantPageContextValue>(
    () => ({
      declaredContext,
      effectiveContext,
      registerPageContext,
    }),
    [declaredContext, effectiveContext, registerPageContext],
  );

  return (
    <AssistantPageContextState.Provider value={value}>
      {children}
    </AssistantPageContextState.Provider>
  );
};

export const useAssistantPageContextState = (): AssistantPageContextValue => (
  useContext(AssistantPageContextState)
);

export const useAssistantPageContext = (context: AssistantPageContext): void => {
  const { registerPageContext } = useAssistantPageContextState();
  const contextKey = [
    context.area,
    context.module,
    context.screen,
    context.mode ?? '',
    context.url ?? '',
  ].join('|');

  useEffect(() => registerPageContext(context), [contextKey, registerPageContext]);
};
